import type { FC } from 'react';
import React, { memo } from 'react';
import { StaticActions as actions } from '@alfa-bank/site-form-utils/actions';

import { formatPhoneValue } from './helpers';
import type { TFormats, TPhone } from './models';

/**
 * Поле ввода номера телефона
 * memo требуется, для того чтоб избежать лишнего
 * ререндера при сабмите, при котором Final Form сбрасывает значения
 * meta.touched, из-за чего пропадают галочки напротив
 * корректно заполненных полей
 */
export const Phone: FC<TPhone> = memo(
    ({
        Field,
        PhoneInput,
        className,
        customValidates,
        formatRules,
        getCaption,
        hasPlaceholderMask,
        hideStatusIcon,
        initialValue,
        isError,
        isNextFocus = true,
        isRequired = true, // для обратной совместимости
        isSuccess,
        mask,
        name,
        onFocus,
        placeholderNumberSymbol,
        queryName,
        settings,
        validatePhone,
    }) => (
        <Field
            data={settings}
            dispatchesFocus
            initialValue={initialValue}
            name={name}
            receivesFocus
            render={({ input, meta }) => {
                const { name: inputName, value: inputValue, ...restInputProps } = input;

                const { noBrackets } = settings;

                /** Для обратной совместимости с пропом noBrackets */
                const phoneFormatRules = noBrackets ? ['noBrackets' as TFormats] : formatRules;
                const inputProps = phoneFormatRules ? restInputProps : input;

                return (
                    <div className={className}>
                        <PhoneInput
                            {...inputProps}
                            caption={getCaption(meta, settings.caption, inputValue)}
                            dataTestId={settings.dataTestId}
                            error={isError(meta, inputValue)}
                            forwardedRef={actions.getRef(meta)}
                            hasPlaceholderMask={hasPlaceholderMask}
                            hideStatusIcon={hideStatusIcon}
                            mask={mask}
                            onBlur={() => {
                                // Чистим поле, если оно необязательно и вернулась только маска
                                if (!isRequired && inputValue === '+7 (') {
                                    actions.onChange(input)('');
                                }

                                actions.onBlur(input)();
                            }}
                            onCancel={actions.onCancel({ input, meta })}
                            onChange={({ value }) => {
                                actions.onChange(input)(String(value));

                                const isComplete =
                                    isNextFocus &&
                                    !validatePhone(isRequired, customValidates)(String(value));

                                if (isComplete) actions.onComplete({ input, meta });
                            }}
                            onFocus={() => {
                                if (onFocus) {
                                    onFocus(input);

                                    return;
                                }
                                actions.onFocus(input)();
                            }}
                            placeholder={settings.placeholder}
                            placeholderNumberSymbol={placeholderNumberSymbol}
                            queryName={queryName}
                            success={isSuccess(meta)}
                            type="tel"
                            value={inputValue}
                        />
                        {phoneFormatRules && (
                            <input
                                name={inputName}
                                type="hidden"
                                value={formatPhoneValue(inputValue, phoneFormatRules)}
                            />
                        )}
                    </div>
                );
            }}
            validate={validatePhone(isRequired, customValidates)}
        />
    )
);
